import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Field } from "./Field.js";
import { Action, ErrorLine } from "./Screen.js";

/**
 * Email and password, and nothing else.
 *
 * Rendered in two places since phase 20a — the front door and `/login` — which
 * is the whole reason it is a component rather than markup inside a page. The
 * *rule* about what a failure may say lives in `hooks/useSignIn.ts`; this knows
 * only how to ask.
 *
 * # It keeps its own field state
 *
 * Presentational does not mean stateless. What is typed into two inputs before
 * they are submitted is nobody else's business, and lifting it would give both
 * pages four props to thread for no reader's benefit. What the component does
 * **not** own is anything the server has an opinion about — the request, the
 * refusal, and where to go afterwards all arrive as props.
 */
export function SignInForm({
  onSubmit,
  busy,
  error,
}: {
  onSubmit: (email: string, password: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit(email, password);
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <Field
        id="email"
        label={t("common.email")}
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="email"
        inputMode="email"
        autoCapitalize="none"
        required
      />
      <Field
        id="password"
        label={t("common.password")}
        type="password"
        value={password}
        onChange={setPassword}
        autoComplete="current-password"
        required
      />
      {error && <ErrorLine>{error}</ErrorLine>}
      <Action disabled={busy || !email.trim() || !password}>{t("login.action")}</Action>
    </form>
  );
}
